import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '../logo.svg';
import { parseJsonSafe } from '../api/client';
import { publicSubmitCohortSignup } from '../api/public';

function extractErrorMessage(data, fallback) {
  return (
    (typeof data?.detail === 'string' && data.detail) ||
    (typeof data?.message === 'string' && data.message) ||
    fallback
  );
}

function CohortSignupPage() {
  const location = useLocation();
  const inviteCode = new URLSearchParams(location.search).get('code') || '';
  const [code, setCode] = useState(inviteCode);
  const [form, setForm] = useState({
    student_email: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    guardian_email: '',
    marketing_consent: null,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [noticeMessage, setNoticeMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field) => (event) => {
    const value = field === 'marketing_consent' ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setNoticeMessage('');
    setIsSubmitting(true);
    try {
      const response = await publicSubmitCohortSignup({ code: code.trim(), ...form });
      const data = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(extractErrorMessage(data, 'Could not submit the sign-up.'));
      }
      setNoticeMessage(
        'Thanks. Your details were submitted to the tutor. They will link your existing Sumrise student account and email the next verification steps.',
      );
      setCode('');
      setForm({
        student_email: '', first_name: '', last_name: '', date_of_birth: '',
        guardian_email: '', marketing_consent: null,
      });
    } catch (error) {
      setErrorMessage(error.message || 'Could not submit the sign-up.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page page-shell auth-page">
      <div className="public-shell">
        <section className="card public-panel">
          <div className="public-panel-copy">
            <div className="public-brand">
              <img src={logo} alt="Sumrise Logo" />
              <span>Sumrise</span>
            </div>
            <p className="public-kicker">Tutor cohort</p>
            <h1>Join your Sumrise cohort</h1>
            <p>
              Use the private link from your tutor to send your details. This form does not create
              an account or grant access; your tutor will link your existing student account.
            </p>
          </div>
          <ul className="public-feature-list">
            <li>
              <strong>Guardian verification</strong>
              <p>For students under 18, a guardian must verify their email and approve access.</p>
            </li>
            <li>
              <strong>One careful match</strong>
              <p>Your tutor selects the exact existing student account before any link request is sent.</p>
            </li>
          </ul>
        </section>

        <section className="card auth-card">
          <div className="auth-card-header">
            <p className="public-kicker">Cohort sign-up</p>
            <p className="muted-text">All fields are required so your tutor can identify the right account.</p>
          </div>

          {noticeMessage ? <div className="notice notice-info" role="status">{noticeMessage}</div> : null}
          {errorMessage ? <div className="notice notice-error" role="alert">{errorMessage}</div> : null}

          <form onSubmit={handleSubmit} className="stack-md">
            <div className="stack-xs">
              <label htmlFor="cohort-signup-code">Tutor invite code</label>
              <input id="cohort-signup-code" value={code} onChange={(event) => setCode(event.target.value)} required />
            </div>
            <div className="stack-xs">
              <label htmlFor="cohort-signup-student-email">Student email</label>
              <input id="cohort-signup-student-email" type="email" autoComplete="email" value={form.student_email} onChange={updateField('student_email')} required />
            </div>
            <div className="auth-link-row">
              <div className="stack-xs" style={{ flex: 1 }}>
                <label htmlFor="cohort-signup-first-name">First name</label>
                <input id="cohort-signup-first-name" autoComplete="given-name" value={form.first_name} onChange={updateField('first_name')} required />
              </div>
              <div className="stack-xs" style={{ flex: 1 }}>
                <label htmlFor="cohort-signup-last-name">Last name</label>
                <input id="cohort-signup-last-name" autoComplete="family-name" value={form.last_name} onChange={updateField('last_name')} required />
              </div>
            </div>
            <div className="stack-xs">
              <label htmlFor="cohort-signup-date-of-birth">Date of birth</label>
              <input id="cohort-signup-date-of-birth" type="date" max={new Date().toISOString().slice(0, 10)} value={form.date_of_birth} onChange={updateField('date_of_birth')} required />
            </div>
            <div className="stack-xs">
              <label htmlFor="cohort-signup-guardian-email">Guardian email</label>
              <input id="cohort-signup-guardian-email" type="email" autoComplete="email" value={form.guardian_email} onChange={updateField('guardian_email')} required />
              <p className="muted-text">For adults, enter a contact email as requested by your tutor.</p>
            </div>
            <fieldset className="stack-xs">
              <legend>Consent to share data that may be used for marketing</legend>
              <label htmlFor="cohort-signup-consent-yes">
                <input id="cohort-signup-consent-yes" name="cohort-signup-consent" type="radio" value="true" checked={form.marketing_consent === true} onChange={() => setForm((current) => ({ ...current, marketing_consent: true }))} required />
                Yes, I consent
              </label>
              <label htmlFor="cohort-signup-consent-no">
                <input id="cohort-signup-consent-no" name="cohort-signup-consent" type="radio" value="false" checked={form.marketing_consent === false} onChange={() => setForm((current) => ({ ...current, marketing_consent: false }))} />
                No, I do not consent
              </label>
              <p className="muted-text">If the student is under 18, a guardian must confirm the final choice by email before it is used.</p>
            </fieldset>
            <button type="submit" className="btn btn-primary auth-action" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit sign-up'}
            </button>
          </form>

          <div className="auth-card-footer">
            <p className="muted-text" style={{ fontSize: 'var(--text-sm)' }}>
              Already have an account? <Link to="/login">Sign in</Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CohortSignupPage;
